import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import ImpersonationServices from "../../../services/ImpersonationServices";
import DashboardLayout from "../../common/DashboardLayout";
import { Icon1, Icon2, User2, course02 } from "../../imagepath";
import { Link } from "react-router-dom";
import AdminServices from "../AdminServices";
import $ from "jquery";
import "datatables.net-dt/css/dataTables.dataTables.min.css";
import "datatables.net";
import { useParams } from "react-router-dom";
import { activationLabel, addedOn } from "../../../utils/teacherStatus";

const SchoolTeacherList = () => {
  const [isClassAdded, setIsClassAdded] = useState([false]);
  const [teachers, setTeachers] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { school_id } = useParams();
  const toggleClass = (index) => {
    const updatedClasses = [...isClassAdded];
    updatedClasses[index] = !updatedClasses[index];
    setIsClassAdded(updatedClasses);
  };
  const fetchTeachers = useCallback(async () => {
    if (!school_id) {
      // No school_id provided, show empty state
      setTeachers([]);
      setTotalPages(0);
      return;
    }
    try {
      const response = await AdminServices.getSchoolTeachers({
        school_id: school_id,
        page: currentPage,
        search,
      });
      console.log("getAllTeachers: ", response);
      setTeachers(response.teachers || []); // Ensure this is an array
      setTotalPages(response.totalPages || 0);
    } catch (error) {
      console.error("Error fetching Teachers:", error);
      setTeachers([]);
      setTotalPages(0);
    }
  }, [school_id, currentPage, search]); // Include school_id as dependency

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]); // fetchTeachers now has stable dependencies
  
  useEffect(() => {
    if (teachers.length === 0) {
      // Clean up DataTable if it exists
      if ($.fn.DataTable.isDataTable("#teachersTable")) {
        $("#teachersTable").DataTable().destroy();
      }
      return;
    }

    if ($.fn.DataTable.isDataTable("#teachersTable")) {
      $("#teachersTable").DataTable().destroy(); // Destroy old instance before reinitializing
    }

    $("#teachersTable").DataTable({
      responsive: true,
      autoWidth: false,
      paging: true, // ✅ Enable Pagination
      searching: true,
      ordering: true,
      pageLength: 10, // ✅ Set default entries per page
      lengthMenu: [[10, 25, 50, -1], [10, 25, 50, "All"]], // ✅ Add pagination options
    });

    $('#teachersTable').closest('.dt-layout-table').addClass('dt-custom-table');
  }, [teachers]);

  return (
    <DashboardLayout
      role="admin"
      layoutType="topnav"
      activeMenu="School Teachers"
      showBreadcrumbs={false}
    >
      <div className="dashboard-title">
        <h4>School Teachers List</h4>
        {school_id && (
          <Link to="/admin/school-lists" className="btn btn-primary">
            ← Back to School List
          </Link>
        )}
      </div>
      
      {!school_id ? (
        // Empty state when no school_id is provided
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="empty-state">
                  <i className="bx bx-user-x" style={{ fontSize: '64px', color: '#ccc', marginBottom: '20px' }}></i>
                  <h5 className="mb-3">No School Selected</h5>
                  <p className="text-muted mb-4">
                    Please select a school from the school list to view its teachers.
                  </p>
                  <Link to="/admin/school-lists" className="btn btn-primary">
                    View School List
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : teachers.length === 0 ? (
        // Empty state when school_id is provided but no teachers found
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="empty-state">
                  <i className="bx bx-user-x" style={{ fontSize: '64px', color: '#ccc', marginBottom: '20px' }}></i>
                  <h5 className="mb-3">No Teachers Found</h5>
                  <p className="text-muted mb-4">
                    This school doesn&apos;t have any teachers yet.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Teachers table when data is available
        <div className="row">
          <div className="custom-table">
            <table
              id="teachersTable"
              className="table table-nowrap mb-0"
            >
              <thead>
                <tr>
                  <th></th>
                  <th>Teacher Name</th>
                  <th>School Name</th>
                  <th>Signed up</th>
                  <th>Added On</th>
                  <th>Payment</th>
                  <th>Payment Date</th>
                  <th>Payment By</th>
                  <th>Course Completed Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {teachers.map((teacher, index) => (
                  <tr key={teacher._id}>
                    <td>{index + 1}</td>
                    <td>{teacher.name}</td>
                    <td>{teacher.school_id ? teacher.school_id.name : "N/A"}</td>
                    <td>{activationLabel(teacher)}</td>
                    <td>{addedOn(teacher)}</td>
                    <td>
                      {teacher.payment?.amount
                        ? `$${teacher.payment.amount.toFixed(2)}`
                        : "N/A"}
                    </td>
                    <td>
                      {teacher.payment?.startDate
                        ? new Date(teacher.payment.startDate).toLocaleString()
                        : "N/A"}
                    </td>
                    <td>
                      {teacher.payment
                        ? teacher.payment.schoolId
                          ? "School"
                          : teacher.payment.userId
                            ? "Individual Teacher"
                            : "N/A"
                        : "N/A"}
                    </td>
                    <td>
                      {teacher?.quizResults?.[0]?.completionDate
                        ? new Date(teacher.quizResults[0].completionDate).toLocaleString()
                        : "N/A"}
                    </td>
                    <td>
                      {/* Support has otherwise had to infer what a customer sees from
                          code and row counts. This opens their actual screens. */}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        title="Open this account read-only, to see what they see"
                        onClick={async () => {
                          const { value: reason } = await Swal.fire({
                            title: `View as ${teacher.name || teacher.email}?`,
                            input: "text",
                            inputLabel: "Why? (recorded in the audit log)",
                            inputPlaceholder: "e.g. checking a reported problem with due dates",
                            html: '<div class="text-muted small text-start">'
                              + 'You will see their screens exactly as they do. '
                              + 'The session is <b>read-only</b> and expires automatically.</div>',
                            showCancelButton: true,
                            confirmButtonText: "View as user"
                          });
                          if (reason === undefined) return;
                          try {
                            const started = await ImpersonationServices.start({
                              userId: teacher._id || teacher.id,
                              email: teacher.email,
                              reason,
                              name: teacher.name,
                              schoolName: teacher.school_id ? teacher.school_id.name : null
                            });
                            window.location.href = started.landing;
                          } catch (error) {
                            Swal.fire({
                              icon: "error",
                              title: "Could not start",
                              text: error.response?.data?.error || error.message
                                || "Failed to start the session."
                            });
                          }
                        }}
                      >
                        View as
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SchoolTeacherList;
